import {
  type FieldPolicy,
  InMemoryCache,
  type InMemoryCacheConfig,
  defaultDataIdFromObject
} from '@apollo/client/cache';

type TDataIdFromObject = NonNullable<InMemoryCacheConfig['dataIdFromObject']>;

/** Страница списка при offset-пагинации: `list(limit, offset, search) → { items total }`. */
interface IOffsetPage {
  items: unknown[];
  total: number;
}

/**
 * Политика поля-списка для «Показать ещё»: страницы склеиваются в один массив
 * по `offset`, `total` берётся из последнего ответа. `keyArgs` — аргументы,
 * которые делят список на разные (обычно `search` и фильтры, без offset/limit).
 *
 * Таблицам с переключением страниц политика не нужна: каждая страница
 * кэшируется отдельно по своим аргументам — это поведение Apollo по умолчанию.
 */
export const offsetPagePagination = (
  keyArgs: FieldPolicy['keyArgs'] = false
): FieldPolicy<IOffsetPage> => ({
  keyArgs,
  merge(existing, incoming, { args }) {
    const offset = typeof args?.offset === 'number' ? args.offset : 0;
    const items = existing ? existing.items.slice(0) : [];

    incoming.items.forEach((item, index) => {
      items[offset + index] = item;
    });

    return { ...incoming, items };
  }
});

/**
 * Ключ сущности в кэше. Бэкенд идентифицирует сущности полем `uuid`, а Apollo
 * по умолчанию ищет `id` — без этого сущности не нормализуются и правка записи
 * не обновляет её в списках. Объекты без `uuid` (страницы списков) лежат внутри
 * родителя, как обычно.
 */
export const dataIdFromObject: TDataIdFromObject = (object, context) =>
  typeof object.uuid === 'string'
    ? `${object.__typename}:${object.uuid}`
    : defaultDataIdFromObject(object, context);

/**
 * Нормализованный кэш Apollo — хранилище серверных данных (в Redux их не копируем).
 * Сущности склеиваются по `__typename` + `uuid`. Политики полей-списков
 * добавляются в `typePolicies.Query.fields`, например:
 * `requests: offsetPagePagination(['search', 'status'])`.
 */
export const createCache = (): InMemoryCache =>
  new InMemoryCache({
    dataIdFromObject,
    typePolicies: {
      Query: {
        fields: {}
      }
    }
  });
